import React from "react";
import Link from "next/link";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";
import type { ContactMethodEntityContext } from "@/lib/utils/crm/contact-method-context";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import type { AppUser } from "@/lib/types/database";
import type { HeaderFact } from "../model";

/**
 * How one `HeaderFact` renders its value.
 *
 * Split out of `DetailHeaderModelView.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/DetailHeaderModelView.behaviour.test.tsx`.
 */

const LINK_CLASS_NAME =
  "text-blue-600 hover:text-blue-700 hover:underline transition-colors truncate";

/** A fact whose value is empty renders only as an inline-add prompt. */
export function isEmptyFact(fact: HeaderFact): boolean {
  return !fact.value || !fact.value.trim();
}

/**
 * Resolves an `app_users.id` UUID (carried in a `kind: "user"` fact's `value`)
 * to a friendly "First Last" / email — a raw UUID must never reach the header.
 */
function UserFactValue({ userId }: { userId: string }) {
  const { data: users, isLoading } = useAdminUsers();

  const displayName = React.useMemo(() => {
    const user = users?.find((u: AppUser) => u.id === userId);
    if (!user) return null;
    return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || null;
  }, [users, userId]);

  if (displayName) return <span className="truncate">{displayName}</span>;
  if (isLoading) return <span className="truncate text-gray-400">…</span>;
  return <span className="truncate text-gray-400">Unknown user</span>;
}

export function FactValue({
  fact,
  entityContext,
}: {
  fact: HeaderFact;
  entityContext?: ContactMethodEntityContext;
}) {
  // A fact whose method belongs to another record (an inherited company line)
  // carries its own context, so the compose / send-text modal logs the activity
  // against that record instead of the one whose header we are rendering.
  const methodContext = fact.methodContext ?? entityContext;

  // Empty editable fact → muted "Add <field>" prompt (the inline-add affordance
  // is supplied by the surrounding HeaderEditableField). Never render a
  // contact-method button / link for an empty value.
  if (isEmptyFact(fact)) {
    return <span className="italic text-gray-400 truncate">{fact.placeholder ?? "Add"}</span>;
  }

  switch (fact.kind) {
    case "user":
      return <UserFactValue userId={fact.value} />;
    case "email":
      return (
        <ContactMethodActions
          kind="email"
          value={fact.value.trim()}
          entityContext={methodContext}
          className={LINK_CLASS_NAME}
        />
      );
    case "phone":
      return (
        <ContactMethodActions
          kind="phone"
          value={fact.value}
          entityContext={methodContext}
          className={LINK_CLASS_NAME}
        />
      );
    case "internal":
      if (!fact.href) return <span className="truncate">{fact.value}</span>;
      return (
        <Link href={fact.href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS_NAME}>
          {fact.value}
        </Link>
      );
    case "external":
      if (!fact.href) return <span className="truncate">{fact.value}</span>;
      return (
        <a href={fact.href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS_NAME}>
          {fact.value}
        </a>
      );
    default:
      return <span className="truncate">{fact.value}</span>;
  }
}
